// useVendorRiskSummary — server-engine adapter for `ExternalBookingsTracker`.
//
// Wraps `get_vendor_risk_summary` over the [startDate, endDate] window the
// Tracker is currently displaying. Mirrors `useSchedulerSummary` shape.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildClientFallbackSummary,
  fetchVendorRiskSummary,
  trackSchedulerEngineFallback,
  type ClientFallbackInput,
  type SchedulerError,
  type SchedulerSource,
  type ServerSchedulerSummary,
} from "@/lib/scheduler";
import type { UseSchedulerState } from "./types";

export interface UseVendorRiskSummaryOptions {
  companyId: string | null | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  clientFallback?: () => ClientFallbackInput;
  enabled?: boolean;
}

export function useVendorRiskSummary(
  options: UseVendorRiskSummaryOptions,
): UseSchedulerState<ServerSchedulerSummary> {
  const { companyId, startDate, endDate, clientFallback, enabled = true } = options;
  const [data, setData] = useState<ServerSchedulerSummary | null>(null);
  const [source, setSource] = useState<SchedulerSource | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SchedulerError | null>(null);
  const [tick, setTick] = useState(0);
  const fallbackRef = useRef(clientFallback);
  fallbackRef.current = clientFallback;

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !companyId || !startDate || !endDate) {
      setData(null);
      setSource(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      const result = await fetchVendorRiskSummary({ companyId, startDate, endDate });
      if (cancelled) return;
      if (result.data) {
        setData(result.data);
        setSource(result.source);
        setError(null);
        setIsLoading(false);
        return;
      }
      const recoverable = !result.error || result.error.recoverable;
      if (recoverable && fallbackRef.current) {
        const fallback = buildClientFallbackSummary(fallbackRef.current());
        setData(fallback);
        setSource("client_fallback");
        setError(null);
        trackSchedulerEngineFallback({
          surface: "external_bookings_tracker",
          scope: "vendor_window",
          reason: result.error?.message ?? "no_data",
          errorCode: result.error?.code,
          recoverable: true,
        });
      } else {
        setData(null);
        setSource(null);
        setError(result.error);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, startDate, endDate, enabled, tick]);

  return useMemo(
    () => ({ data, source, isLoading, error, refetch }),
    [data, source, isLoading, error, refetch],
  );
}
